import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatCurrency } from '@tally/shared';
import { CalendarClock, CalendarRange, Hash, RefreshCw, Repeat, Wallet } from 'lucide-react';
import { subscriptionsApi, type SubscriptionItem } from '@/services/api';
import { ROUTES } from '@/routes';
import { cn } from '@/utils/cn';
import { formatUsd } from '@/utils/money';
import { durationLabel, monthLabel, monthsBetween, todayIso } from '@/utils/time';
import { AXIS_TICK, CHART_TOOLTIP, SERIES_COLORS, compactUsd } from '@/components/charts/chartTheme';

type Filter = 'active' | 'ended' | 'all';

const CADENCE_LABEL: Record<SubscriptionItem['cadence'], string> = { weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };
const PER: Record<SubscriptionItem['cadence'], string> = { weekly: 'wk', monthly: 'mo', quarterly: 'qtr', yearly: 'yr' };

/** Where "View charges" goes: the subscription's tag when it has one, else a merchant search. */
export function chargesLink(s: SubscriptionItem): string {
  const params = new URLSearchParams(s.tagId ? { tagId: s.tagId } : { searchQuery: s.merchant });
  return `${ROUTES.app.transactions}?${params.toString()}`;
}

export function SubscriptionsPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>('active');
  const { data, isLoading } = useQuery({ queryKey: ['subscriptions'], queryFn: () => subscriptionsApi.list() });
  const refresh = useMutation({
    mutationFn: () => subscriptionsApi.refresh(),
    onSuccess: () => {
      for (const key of ['subscriptions', 'tags', 'transactions']) queryClient.invalidateQueries({ queryKey: [key] });
    },
  });

  const items = data?.data?.items ?? [];
  const summary = data?.data?.summary;
  const shown = items.filter((s) => (filter === 'all' ? true : filter === 'active' ? s.active : !s.active));
  const chart = items
    .filter((s) => s.active && s.yearlyUsd)
    .slice(0, 10)
    .map((s) => ({ name: s.merchant, yearly: s.yearlyUsd ?? 0 }));
  const longest = items.reduce<SubscriptionItem | null>((a, s) => (!a || s.firstDate < a.firstDate ? s : a), null);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Subscriptions</h1>
          <p className="text-sm text-muted-foreground">Charges that repeat on a schedule, found in your expenses automatically</p>
        </div>
        <button
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
        >
          <RefreshCw className={cn('h-4 w-4', refresh.isPending && 'animate-spin')} /> {refresh.isPending ? 'Updating…' : 'Update tags'}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={Wallet} label="Per month" value={formatUsd(summary?.monthlyUsd)} hint="active subscriptions" />
        <Stat icon={CalendarRange} label="Per year" value={formatUsd(summary?.yearlyUsd)} hint="at current prices" />
        <Stat icon={Hash} label="Active" value={String(summary?.activeCount ?? 0)} hint={`${items.length - (summary?.activeCount ?? 0)} ended`} />
        <Stat
          icon={CalendarClock}
          label="Spent so far"
          value={formatUsd(summary?.totalUsd)}
          hint={longest ? `since ${monthLabel(longest.firstDate)}` : 'all subscriptions'}
        />
      </div>

      {chart.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
          <h3 className="mb-4 text-sm font-semibold">Yearly cost of active subscriptions</h3>
          <div style={{ height: Math.max(160, chart.length * 36) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={AXIS_TICK} tickFormatter={compactUsd} />
                <YAxis type="category" dataKey="name" tick={AXIS_TICK} width={120} />
                <Tooltip {...CHART_TOOLTIP} formatter={(v: number) => [formatUsd(v), 'Per year']} />
                <Bar dataKey="yearly" radius={[0, 6, 6, 0]}>
                  {chart.map((_, i) => <Cell key={i} fill={SERIES_COLORS[i % SERIES_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 rounded-lg border border-border p-1 w-fit" role="tablist">
        {(['active', 'ended', 'all'] as Filter[]).map((f) => (
          <button
            key={f}
            role="tab"
            aria-selected={filter === f}
            onClick={() => setFilter(f)}
            className={cn('rounded-md px-3 py-1.5 text-sm font-medium capitalize', filter === f ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground')}
          >
            {f}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[...Array(6)].map((_, i) => <div key={i} className="h-44 animate-pulse rounded-xl bg-muted" />)}
        </div>
      ) : shown.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          {items.length === 0
            ? 'No repeating charges found yet. They show up once a merchant has charged about the same amount a few times on a schedule.'
            : `No ${filter} subscriptions.`}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((s) => <SubscriptionCard key={s.key} s={s} />)}
        </div>
      )}
    </div>
  );
}

function SubscriptionCard({ s }: { s: SubscriptionItem }) {
  const end = s.active ? todayIso() : s.lastDate;
  return (
    <div className={cn('flex flex-col rounded-xl border border-border bg-card p-5', !s.active && 'opacity-75')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold" title={s.merchant}>{s.merchant}</p>
          <p className="text-xs text-muted-foreground">
            <Repeat className="mr-1 inline h-3 w-3" />{CADENCE_LABEL[s.cadence]} · {s.count} charge{s.count === 1 ? '' : 's'}
          </p>
        </div>
        <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', s.active ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-muted text-muted-foreground')}>
          {s.active ? 'Active' : 'Ended'}
        </span>
      </div>

      <p className="mt-3 text-2xl font-bold">
        {formatCurrency(s.typicalAmount, s.currency)}
        <span className="text-sm font-normal text-muted-foreground">/{PER[s.cadence]}</span>
      </p>

      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
        <div>
          <dt className="text-xs text-muted-foreground">Per month</dt>
          <dd className="font-medium">{s.monthlyUsd === null ? '—' : formatUsd(s.monthlyUsd)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Per year</dt>
          <dd className="font-medium">{s.yearlyUsd === null ? '—' : formatUsd(s.yearlyUsd)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Paying for</dt>
          <dd className="font-medium">{durationLabel(monthsBetween(s.firstDate, end))}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Spent in total</dt>
          <dd className="font-medium">{formatUsd(s.totalUsd)}</dd>
        </div>
      </dl>

      <div className="mt-4 flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
        <span>
          Since {monthLabel(s.firstDate)} · {s.active ? `next ~${s.nextExpected}` : `last ${s.lastDate}`}
        </span>
        <Link to={chargesLink(s)} className="font-medium text-primary hover:underline">View charges</Link>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint }: { icon: typeof Wallet; label: string; value: string; hint: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <p className="mt-1 text-xl font-bold sm:text-2xl">{value}</p>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
